import { useState, useEffect, lazy, Suspense } from "react";
import { Menu, X, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";

const BookingModal = lazy(() => import("@/components/BookingModal"));

const Navbar = () => {
  const { t, lang, toggleLanguage, dir } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const navLinks = [
    { name: t.nav.foundationSession, href: "#foundation-session", isHighlight: true },
    { name: t.nav.testimonials, href: "#testimonials" },
    { name: t.nav.services, href: "/services", isRoute: true },
    { name: t.nav.blog, href: "/blog", isRoute: true },
    { name: t.nav.aboutDr, href: "/about", isRoute: true },
    { name: t.nav.contact, href: "#contact" },
  ];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (link: typeof navLinks[0]) => {
    setIsMobileMenuOpen(false);
    if ((link as any).isRoute) {
      navigate(link.href);
      return;
    }
    if (location.pathname !== "/") {
      navigate("/" + link.href);
      return;
    }
    setTimeout(() => {
      const element = document.querySelector(link.href);
      if (element) {
        const navbarHeight = 80;
        const elementPosition = element.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: elementPosition - navbarHeight, behavior: "smooth" });
      }
    }, 350);
  };

  return (
    <>
      <nav
        aria-label={t.nav.mainNav}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-background/95 backdrop-blur-md shadow-md border-b border-border"
            : "bg-background/80 backdrop-blur-sm border-b border-border/50"
        }`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <a
              href="#"
              className="font-serif font-bold text-foreground group"
              onClick={(e) => {
                e.preventDefault();
                if (location.pathname !== "/") {
                  navigate("/");
                } else {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
            >
              <span className="block text-lg font-bold text-center group-hover:text-primary transition-colors" style={{ minHeight: '1.75rem', lineHeight: '1.75rem' }}>{t.nav.logoName}</span>
              <span className="block text-[10px] leading-tight text-muted-foreground font-serif text-center font-bold">
                {t.nav.logoTitle}
              </span>
            </a>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => {
                const isActive = (link as any).isRoute
                  ? location.pathname === link.href
                  : false;
                return (
                  <button
                    key={link.name}
                    onClick={() => handleNavClick(link)}
                    className={`relative text-sm font-medium transition-all px-3 py-2 rounded-lg ${
                      (link as any).isHighlight
                        ? "text-primary font-bold hover:bg-primary/10"
                        : isActive
                        ? "text-primary font-semibold"
                        : "text-foreground hover:text-primary hover:bg-primary/5"
                    }`}
                  >
                    {link.name}
                    {isActive && (
                      <motion.span
                        layoutId="nav-underline"
                        className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary rounded-full"
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                  </button>
                );
              })}

              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-primary transition-all px-3 py-2 rounded-lg hover:bg-primary/5 border border-transparent hover:border-border"
                aria-label={lang === "ar" ? "Switch to English" : "التبديل للعربية"}
              >
                <Globe className="w-4 h-4" />
                {lang === "ar" ? "EN" : "عربي"}
              </button>

              <Button
                onClick={() => setBookingOpen(true)}
                size="sm"
                className="font-bold text-sm px-5 shadow-md ms-2"
              >
                {t.nav.bookSession}
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center gap-2">
              {/* Language Toggle Mobile */}
              <button
                onClick={toggleLanguage}
                className="p-2 min-w-[44px] min-h-[44px] text-foreground flex items-center justify-center"
                aria-label={lang === "ar" ? "Switch to English" : "التبديل للعربية"}
              >
                <Globe className="w-5 h-5" />
              </button>
              <button
                className="p-3 min-w-[48px] min-h-[48px] text-foreground flex items-center justify-center"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label={isMobileMenuOpen ? t.nav.closeMenu : t.nav.openMenu}
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden glass border-t border-border"
            >
              <div className="container mx-auto px-4 py-4 flex flex-col gap-2">
                {navLinks.map((link) => (
                  <button
                    key={link.name}
                    onClick={() => handleNavClick(link)}
                    className={`py-3 px-4 font-semibold text-base rounded-md text-foreground hover:bg-primary/10 min-h-[48px] flex items-center ${dir === "rtl" ? "text-right justify-end" : "text-left justify-start"}`}
                  >
                    {link.name}
                  </button>
                ))}
                <Button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    setBookingOpen(true);
                  }}
                  size="lg"
                  className="w-full font-bold text-base mt-2 shadow-md"
                >
                  {t.nav.bookSessionChange}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {bookingOpen && (
        <Suspense fallback={null}>
          <BookingModal open={bookingOpen} onOpenChange={setBookingOpen} />
        </Suspense>
      )}
    </>
  );
};

export default Navbar;
